import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import type { Block } from '@gallaghercp/core-frontend';
import {
  AppState,
  AuthService,
  BlockAbstractComponent,
  ParserService,
  WindowService,
} from '@gallaghercp/core-frontend';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'core-coro-accordion',
  templateUrl: './coro-accordion.component.html',
  styleUrl: './coro-accordion.component.scss',
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class CoroAccordionComponent extends BlockAbstractComponent implements OnInit, OnDestroy {
  protected override parser: ParserService;
  protected override auth: AuthService;
  protected store = inject<Store<AppState>>(Store);
  protected windowService = inject(WindowService);

  public panelStates!: boolean[];
  @ViewChildren(MatExpansionPanel, { read: ElementRef })
  panels!: QueryList<ElementRef>;

  subscription: Subscription = new Subscription();

  constructor() {
    const parser = inject(ParserService);
    const auth = inject(AuthService);

    super(parser, auth);

    this.parser = parser;
    this.auth = auth;
  }

  override ngOnInit() {
    super.ngOnInit();
    this.panelStates = new Array((this.block.content ?? []).length).fill(false);
    this.block.content = (this.block.content ?? []).map((block: Block) => {
      if (block.data?.isAnchor && !block.data.anchorId) {
        block.data.anchorId = (block.data.title ?? '')
          .toLowerCase()
          .replace(/\s+/g, '-') // Replace spaces with -
          .replace(/[^a-z0-9-]/g, '') // Remove all non-word chars except hyphens
          .replace(/-+/g, '-'); // Replace multiple - with single -
      }
      return block;
    });
  }

  setState(i: number, state: boolean): void {
    this.panelStates[i] = state;
  }

  onPanelOpened(index: number): void {
    this.subscription.add(
      this.store
        .select((state: AppState): boolean => !!state.app['accordionPanelTransition'])
        .pipe(take(1))
        .subscribe(hasTransition => {
          if (hasTransition) {
            const panelElement = this.panels.toArray()[index];
            if (panelElement) {
              setTimeout(() => {
                const win = this.windowService.nativeWindow;

                if (win) {
                  const elementPosition =
                    (panelElement.nativeElement?.getBoundingClientRect().top ?? 0) + (win.scrollY ?? 0);
                  const offsetPosition = elementPosition - 100; // Subtract the offset

                  win.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth',
                  });
                }
              }, 250);
            }
          }

          this.setState(index, true);
        }),
    );
  }

  getPanelState(i: number): string {
    return this.panelStates[i] ? 'expand' : 'collapse';
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscription.unsubscribe();
  }
}
